import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { testimonialsData } from '../data/mock';

/**
 * Full-width auto-scrolling reviews marquee.
 *
 * - Auto-scrolls right-to-left continuously (~5s per card → linear marquee).
 * - Pauses on hover, focus, or user interaction with prev/next buttons.
 * - Resumes ~4s after the visitor stops interacting.
 * - On mobile, native horizontal scroll is supported; the auto-scroll pauses
 *   for ~4s after the user releases the scroll so manual browsing always wins.
 *
 * Why duplicate the card list? CSS marquee needs the items duplicated so the
 * `translateX(-50%)` end-state lines up seamlessly with the start-state and
 * the loop has no visible "jump".
 */
const ReviewsCarousel = () => {
  const [isPaused, setIsPaused] = useState(false);
  const idleTimerRef = useRef(null);
  const trackRef = useRef(null);

  const SECONDS_PER_CARD = 5;
  const RESUME_AFTER_IDLE_MS = 4000;
  const totalDurationSec = testimonialsData.length * SECONDS_PER_CARD;

  const pauseAndResume = useCallback(() => {
    setIsPaused(true);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => setIsPaused(false), RESUME_AFTER_IDLE_MS);
  }, []);

  useEffect(() => () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
  }, []);

  // Manual stepping: nudge the track by ~1 card-width then pause + resume.
  const step = useCallback((direction) => {
    pauseAndResume();
    const node = trackRef.current;
    if (!node) return;
    const firstCard = node.querySelector('.review-marquee-card');
    if (!firstCard) return;
    const cardWidth = firstCard.getBoundingClientRect().width
      + parseFloat(getComputedStyle(node).gap || '0');
    node.scrollBy({ left: direction * cardWidth, behavior: 'smooth' });
  }, [pauseAndResume]);

  return (
    <section
      className="reviews-marquee-section"
      aria-label="Recent patient reviews"
      data-testid="reviews-marquee"
    >
      <div
        className="reviews-marquee-viewport"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onFocus={() => setIsPaused(true)}
        onBlur={() => setIsPaused(false)}
        onTouchStart={pauseAndResume}
        onScroll={pauseAndResume}
      >
        <button
          type="button"
          className="reviews-marquee-nav reviews-marquee-nav-prev"
          onClick={() => step(-1)}
          aria-label="Previous review"
          data-testid="reviews-marquee-prev"
        >
          <ChevronLeft size={18} />
        </button>

        <div
          className={`reviews-marquee-track ${isPaused ? 'is-paused' : ''}`}
          ref={trackRef}
          data-testid="reviews-marquee-track"
          style={{ '--marquee-duration': `${totalDurationSec * 2}s` }}
        >
          {/* Duplicated twice for seamless looping */}
          {[...testimonialsData, ...testimonialsData].map((review, idx) => (
            <article
              key={`marquee-${review.id}-${idx}`}
              className="review-marquee-card"
              aria-hidden={idx >= testimonialsData.length ? 'true' : undefined}
            >
              <div className="review-marquee-stars" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star
                    key={`star-${review.id}-${idx}-${i}`}
                    size={14}
                    fill="currentColor"
                  />
                ))}
              </div>
              <p className="review-marquee-text">{review.text}</p>
              <footer className="review-marquee-footer">
                <span className="review-marquee-avatar" aria-hidden="true">
                  {review.name.charAt(0)}
                </span>
                <span className="review-marquee-meta">
                  <strong>{review.name}</strong>
                  <small>{review.condition}</small>
                </span>
              </footer>
            </article>
          ))}
        </div>

        <button
          type="button"
          className="reviews-marquee-nav reviews-marquee-nav-next"
          onClick={() => step(1)}
          aria-label="Next review"
          data-testid="reviews-marquee-next"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </section>
  );
};

export default ReviewsCarousel;
